import Button from '@ui-library/button';
import ConfirmChanges from '@ui-library/modal/confirmChanges';
import { SubHeader } from '@components/sources/source/warehouseSource/configureData/style';
import {
  mappingSupportType,
  mappingType,
} from '@components/sources/visualizer/mappingScreens/schemaEditor';
import useModalView from '@hooks/useModalView';
import { observer } from 'mobx-react';
import queryString from 'query-string';
import React from 'react';
import { useHistory } from 'react-router-dom';
import Schema from '@components/sources/visualizer/mappingScreens/schema';
import classes from './sync-schema.module.scss';
import NoDestinationTabView from '../source-destinations/noDestinationTabView';
import useSourceSchema from './hooks';

const UPDATE_MODAL_TEXT =
  'These changes might affect the way your pipeline and ' +
  'destination schema operates. Please confirm that you want these changes to ' +
  'be committed.';
const CANCEL_MODAL_TEXT =
  'You’re about to leave this page with unsaved changes. Are you sure you want to continue?';

type Props = { isAdmin: boolean };

const SourceSchema = ({ isAdmin }: Props) => {
  const history = useHistory();
  const parsed = queryString.parse(history.location.search);

  const {
    edit,
    loading,
    setLoading,
    isValid,
    setValid,
    mappingTypeValue,
    setMappingTypeValue,
    mappingSupportTypeVal,
    setMappingSupportTypeVal,
    bottomEditButtonVisible,
    setBottomEditButtonVisible,
    schemaErrors,
    message,
    setMessage,
    destination,
    onCancel,
    onSubmit,
    childRef,
    handleEditClick,
    updateSchemaErrors,
    source,
  } = useSourceSchema(parsed);

  const [updateModal, showUpdateModal] = useModalView(
    <ConfirmChanges
      onConfirm={() => {
        onSubmit(mappingSupportTypeVal);
        updateSchemaErrors([]);
      }}
      onCancel={() => showUpdateModal(false)}
      type="save"
      text={UPDATE_MODAL_TEXT}
    />,
  );
  const [cancelModal, showCancelModal] = useModalView(
    <ConfirmChanges
      onConfirm={onCancel}
      onCancel={() => showCancelModal(false)}
      type="cancel"
      text={CANCEL_MODAL_TEXT}
    />,
  );

  const schemaViewOrEdit = (
    <Schema
      edit={edit}
      ref={childRef}
      setMessage={setMessage}
      setMappingTypeValue={setMappingTypeValue}
      mappingSupportTypeVal={mappingSupportTypeVal}
      source={source}
      destination={destination}
      mappingTypeValue={mappingTypeValue}
      setMappingSupportTypeVal={setMappingSupportTypeVal}
      setBottomEditButtonVisible={setBottomEditButtonVisible}
      ifUpdate={!parsed.destinationId}
      setValid={setValid}
      schemaErrors={schemaErrors}
      updateSchemaErrors={updateSchemaErrors}
    />
  );

  const saveOrUpdateButton = (
    <div className={classes.header_cta}>
      {isAdmin && !edit && <Button secondary onClick={handleEditClick} name="Update" />}
      {edit && <SubHeader className={classes.error_message_wrapper}>{message}</SubHeader>}
      {edit && <Button secondary onClick={() => showCancelModal(true)} name="Cancel" />}
      {edit && (
        <Button
          disabled={
            mappingTypeValue === mappingType.calculating ||
            (mappingTypeValue === mappingType.oldMappingSelected && !isValid) ||
            (mappingSupportTypeVal === mappingSupportType.updating &&
              mappingTypeValue === mappingType.newMappingSelected &&
              !bottomEditButtonVisible)
          }
          className={classes.header_cta__save}
          onClick={async () => {
            if (childRef?.current && !!childRef?.current?.whActionMappingsValidate) {
              setLoading(true);
              const updatedConfig = await childRef?.current?.whActionMappingsValidate(false, false);
              if (!updatedConfig.errorMessage) {
                showUpdateModal(true);
                setMessage('');
                setLoading(false);
              } else {
                setMessage(updatedConfig.errorMessage);
                setLoading(false);
              }
            } else {
              showUpdateModal(true);
            }
          }}
          loading={loading}
          name="Save"
        />
      )}
    </div>
  );

  if (source?.destinations?.length === 1 || parsed?.destinationId) {
    return (
      <div>
        {updateModal}
        {cancelModal}
        <div className={classes.header}>
          <div className={classes.header_info}>
            <div className={classes.header_info__main_text}>
              {!edit ? 'Current' : 'Update'} configuration
            </div>
            {edit ? (
              <div className={classes.header_info__sub_text}>
                Update the warehouse columns, destination fields in the Map fields section.
              </div>
            ) : null}
          </div>
          {saveOrUpdateButton}
        </div>
        <div className={`${classes.schema_data_container} ${!edit ? classes.view_mode : ''}`}>
          {schemaViewOrEdit}
        </div>
        {mappingTypeValue === mappingType.newMappingSelected && bottomEditButtonVisible && (
          <div className={classes.header}>
            {saveOrUpdateButton}
            <div className={classes.footer} />
          </div>
        )}
      </div>
    );
  }
  return <NoDestinationTabView tabName="SCHEMA" sourceID={source?.id} />;
};

export default observer(SourceSchema);
